// BRK-S171 — two things a roster can be wrong about as a WHOLE, checked before
// the save and rendered on the cards they are about.
//
// ── WHY THESE TWO, AND WHY HERE ─────────────────────────────────────────────
//
// Every other rule the roster enforces is about ONE field: is it answered, is
// it a valid date, is it nine digits. `bondFields.ts` owns those, and it can,
// because each is a question you can ask of a value on its own.
//
// These two cannot be asked that way. "Do the shares add up to more than 100%"
// is a question about the cap table, and "do two people share an email" is a
// question about the pair — neither has a single offending field, and both name
// PEOPLE rather than inputs. So they live here, they return the OWNER IDS they
// are about, and the page renders them on those cards through the same
// `cardErrors` channel a refused per-card save already uses (BRK-S164).
//
// ── THE SERVER IS THE AUTHORITY; THIS IS THE ROUND TRIP SAVED ───────────────
//
// PMS refuses both with structured codes (`OWNERSHIP_EXCEEDS_TOTAL`,
// `DUPLICATE_ROSTER_EMAIL`), and those refusals are read here too — the same
// findings, from the other direction, rendered in the same place. Checking
// first only means the broker learns before the round trip; it never means this
// is the only gate. A client that let one through still gets the refusal, and
// the refusal still lands on the right card.
//
// ── NO PII BEYOND WHAT IS ALREADY ON SCREEN ─────────────────────────────────
//
// A duplicate-email message names the email, which the broker typed and is
// looking at. It names nothing else: not the other person's name, not their
// share, not anything from the other card. `formatShare` is reused for the
// total so the bar under the roster and this sentence cannot disagree about
// what 33.335 is.
import { ApiError } from "@/lib/api";
import type { BondRenderField, BondValueBag } from "@/lib/bondFields";
import { asText } from "@/lib/bondFields";
import {
  OWNERSHIP_PATH,
  UNNAMED,
  formatShare,
  ownerPeople,
  scopedRosterName,
  type RosterPerson,
} from "@/lib/bondRoster";

/** The shares of the LISTED owners may not exceed the whole of the company. */
export const OWNERSHIP_TOTAL_LIMIT = 100;

export const OWNERSHIP_EXCEEDS_TOTAL = "OWNERSHIP_EXCEEDS_TOTAL";
export const DUPLICATE_ROSTER_EMAIL = "DUPLICATE_ROSTER_EMAIL";

/**
 * One thing wrong with the roster as a whole, and whose cards it is about.
 *
 * `ownerIds` is never empty: a finding with nobody to attach to would be a
 * page-level toast by another name, which is exactly what this replaces.
 */
export interface RosterFinding {
  code: typeof OWNERSHIP_EXCEEDS_TOTAL | typeof DUPLICATE_ROSTER_EMAIL;
  ownerIds: string[];
  message: string;
}

export function ownershipExceedsMessage(total: number): string {
  return `The owners' shares add up to ${formatShare(total)}%. They cannot total more than ${OWNERSHIP_TOTAL_LIMIT}%.`;
}

export function duplicateEmailMessage(email: string): string {
  return `${email} is already used by someone else on this request. Each person needs their own email address.`;
}

/**
 * The combined share of the LISTED OWNERS.
 *
 * **Spouses are excluded, and that is the whole subtlety.** A spouse is drawn
 * from the same `personal[]` block as an owner and can therefore carry an
 * `ownershipPercentage` in the bag — left over from a row that was an owner, or
 * simply never cleared. Counting it would tell a broker their cap table adds up
 * to 160% when the two people who own the company hold 80% each. `ownerPeople`
 * is the roster's own answer to "who is an owner", so this asks it rather than
 * re-deciding.
 *
 * A non-numeric share counts as nothing. It is not this function's job to
 * report that a share is unreadable — `fieldError` already refuses it — and
 * guessing would put a number in a sentence nobody typed.
 */
export function ownershipTotal(
  roster: readonly RosterPerson[],
  values: BondValueBag,
): number {
  let total = 0;
  for (const owner of ownerPeople(roster)) {
    const value = values[scopedRosterName(owner.ownerId, OWNERSHIP_PATH)];
    if (typeof value === "number" && Number.isFinite(value)) total += value;
  }
  return total;
}

/**
 * The path that carries a person's email, as the CONFIGURATION declares it —
 * or null when this step asks for none.
 *
 * Found by declared **format**, never by name (D-10 rule 1: "never infer a
 * control from a field's name"). A configuration that spells it
 * `personal[].emailAddress` still gets the duplicate check, and a step with no
 * email field simply has nothing to check rather than a rule keyed to a path
 * nobody declared.
 *
 * The fields handed in are the roster's SCOPED ones, so the contract path is
 * recovered from the first match and re-scoped per person by the caller.
 */
function emailPathOf(fields: readonly BondRenderField[]): string | null {
  const match = fields.find((field) => field.format === "email");
  if (!match) return null;
  const marker = "::";
  const at = match.name.indexOf(marker);
  return at === -1 ? match.name : match.name.slice(at + marker.length);
}

/**
 * Everything wrong with this roster, before anything is sent.
 *
 * Both checks at once rather than first-wins: they are about different people,
 * and reporting one, being fixed, and then being told about the other is two
 * round trips through a form for one save.
 */
export function rosterFindings(
  roster: readonly RosterPerson[],
  fields: readonly BondRenderField[],
  values: BondValueBag,
): RosterFinding[] {
  const findings: RosterFinding[] = [];

  const total = ownershipTotal(roster, values);
  if (total > OWNERSHIP_TOTAL_LIMIT) {
    // Every owner HOLDING A SHARE, because the excess is theirs collectively —
    // there is no one owner who caused it, and putting it on the last one
    // edited would name somebody arbitrarily.
    const holders = ownerPeople(roster).filter((owner) => {
      const value = values[scopedRosterName(owner.ownerId, OWNERSHIP_PATH)];
      return typeof value === "number" && Number.isFinite(value) && value > 0;
    });
    if (holders.length > 0) {
      findings.push({
        code: OWNERSHIP_EXCEEDS_TOTAL,
        ownerIds: holders.map((owner) => owner.ownerId),
        message: ownershipExceedsMessage(total),
      });
    }
  }

  const emailPath = emailPathOf(fields);
  if (emailPath !== null) {
    // Case-insensitively, because an address is not case-sensitive in the part
    // that matters and a broker typing one person's email twice will not type
    // it identically. A BLANK never collides: several people not having
    // supplied one is not a duplicate, and refusing it would block a save over
    // a field the configuration may not even require.
    const seen = new Map<string, string[]>();
    for (const person of roster) {
      const raw = asText(values[scopedRosterName(person.ownerId, emailPath)]);
      const key = raw.trim().toLowerCase();
      if (key === "") continue;
      const owners = seen.get(key) ?? [];
      owners.push(person.ownerId);
      seen.set(key, owners);
    }
    for (const [key, owners] of seen) {
      if (owners.length < 2) continue;
      findings.push({
        code: DUPLICATE_ROSTER_EMAIL,
        // Every card that shares it, not just the first two: three people on
        // one address is one mistake, and telling the broker about two of them
        // leaves a save that fails again.
        ownerIds: owners,
        message: duplicateEmailMessage(key),
      });
    }
  }

  return findings;
}

/**
 * The same two findings, read back off the server's refusal.
 *
 * Read DEFENSIVELY, exactly as `serverFieldErrors.ts` reads every other code: a
 * body that is not this shape returns nothing and the caller falls back to the
 * step-level message, because a refusal reported in the wrong place is worse
 * than one reported generically.
 *
 * `DUPLICATE_ROSTER_EMAIL` names its `ownerIds`, so it anchors exactly.
 * `OWNERSHIP_EXCEEDS_TOTAL` carries only the `total` — there is no one owner to
 * blame and the contract does not pretend otherwise — so it anchors to every
 * owner holding a share, which is the same set the client-side check names and
 * the honest answer to "whose is this about".
 */
export function rosterFindingsFromError(
  error: unknown,
  roster: readonly RosterPerson[],
  values: BondValueBag,
): RosterFinding[] {
  if (!(error instanceof ApiError)) return [];
  const body = error.body;
  if (typeof body !== "object" || body === null) return [];
  const details = (body as Record<string, unknown>).details;
  if (typeof details !== "object" || details === null) return [];
  const record = details as Record<string, unknown>;

  if (record.code === OWNERSHIP_EXCEEDS_TOTAL) {
    const total =
      typeof record.total === "number" && Number.isFinite(record.total)
        ? record.total
        : ownershipTotal(roster, values);
    const holders = ownerPeople(roster).filter((owner) => {
      const value = values[scopedRosterName(owner.ownerId, OWNERSHIP_PATH)];
      return typeof value === "number" && Number.isFinite(value) && value > 0;
    });
    // Nobody to anchor to means the server is refusing over a cap table this
    // screen is not drawing. Say nothing here and let the step-level message
    // carry it, rather than inventing a card.
    if (holders.length === 0) return [];
    return [
      {
        code: OWNERSHIP_EXCEEDS_TOTAL,
        ownerIds: holders.map((owner) => owner.ownerId),
        message: ownershipExceedsMessage(total),
      },
    ];
  }

  if (record.code === DUPLICATE_ROSTER_EMAIL) {
    const named = Array.isArray(record.ownerIds)
      ? record.ownerIds.filter((id): id is string => typeof id === "string")
      : [];
    // Only ids this roster is actually drawing: an id for somebody off-screen
    // has no card, and the step-level message is where that belongs.
    const known = new Set(roster.map((person) => person.ownerId));
    const ownerIds = named.filter((id) => known.has(id));
    if (ownerIds.length === 0) return [];
    const email =
      typeof record.email === "string" ? record.email : "That email address";
    return [
      {
        code: DUPLICATE_ROSTER_EMAIL,
        ownerIds,
        message: duplicateEmailMessage(email),
      },
    ];
  }

  return [];
}

/**
 * Findings collapsed onto the `cardErrors` channel the roster already renders.
 *
 * One sentence per card. Two findings about one person join with a space rather
 * than one silently replacing the other — a broker who fixes the share and
 * saves again should not be meeting the duplicate email for the first time.
 */
export function rosterCardErrors(
  findings: readonly RosterFinding[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const finding of findings) {
    for (const ownerId of finding.ownerIds) {
      errors[ownerId] = errors[ownerId]
        ? `${errors[ownerId]} ${finding.message}`
        : finding.message;
    }
  }
  return errors;
}

// ── BRK-S317: no two people on the roster share an SSN ─────────────────────
//
// Daniel's walk (week of 10/4): the primary indemnitor's SSN and the spouse's
// could be identical and the wizard proceeded. Unlike the two checks above,
// this one DOES have an offending field — the SSN being typed — so it is
// rendered INLINE on that field and keeps every Save disabled until it
// differs, rather than riding the card-level channel. SUR-S220 refuses the
// same save as `SSN_DUPLICATE` (`details.fields` naming both people), which
// `anchorServerError` places on the same fields: this is the round trip saved.
//
// Compared on DIGITS (formatting ignored). A stored SSN never reaches the
// browser, only its last four, so a typed SSN is compared against a masked one
// on the last four — enough to catch an identical entry, and it never needs the
// full number. Two stored values are never compared here: neither is being
// edited, and the server, which holds both, is the one that can say.

export const SSN_DUPLICATE = "SSN_DUPLICATE";

export const SPOUSE_SSN_MATCHES_OWNER =
  "The spouse's SSN must differ from the owner's.";

export function duplicateSsnMessage(name: string): string {
  return `This SSN is already entered for ${name}. Each person needs their own SSN.`;
}

/** The SSN path as the configuration declares it, found by `format: "ssn"`
 *  (D-10 rule 1) — the `emailPathOf` rule. */
function ssnPathOf(fields: readonly BondRenderField[]): string | null {
  const match = fields.find((field) => field.format === "ssn");
  if (!match) return null;
  const marker = "::";
  const at = match.name.indexOf(marker);
  return at === -1 ? match.name : match.name.slice(at + marker.length);
}

interface SsnReading {
  person: RosterPerson;
  name: string;
  /** All nine digits, when the browser has them (typed, or held this session). */
  digits: string | null;
  last4: string;
  /** Being typed — the side that can be fixed, and so the side that is told. */
  typed: boolean;
}

function readSsn(
  person: RosterPerson,
  field: BondRenderField,
  values: BondValueBag,
  held: (name: string) => string | undefined,
): SsnReading | null {
  const typed = asText(values[field.name]).replace(/\D/g, "");
  if (typed.length === 9) {
    return {
      person,
      name: field.name,
      digits: typed,
      last4: typed.slice(-4),
      typed: true,
    };
  }
  // Partly typed: the nine-digit rule owns that complaint, not this one.
  if (asText(values[field.name]).trim() !== "") return null;
  const kept = (held(field.name) ?? "").replace(/\D/g, "");
  if (kept.length === 9) {
    return {
      person,
      name: field.name,
      digits: kept,
      last4: kept.slice(-4),
      typed: false,
    };
  }
  const last4 = field.storedSsn?.last4;
  return last4
    ? { person, name: field.name, digits: null, last4, typed: false }
    : null;
}

function sameSsn(a: SsnReading, b: SsnReading): boolean {
  if (a.digits !== null && b.digits !== null) return a.digits === b.digits;
  if (a.digits === null && b.digits === null) return false;
  return a.last4 === b.last4;
}

/** How a sentence names a person on another row — their typed name, or what
 *  they are on this request when nothing has been typed. */
function rowName(person: RosterPerson): string {
  if (person.displayName && person.displayName !== UNNAMED) {
    return person.displayName;
  }
  return person.spouseOf === null
    ? "another owner on this request"
    : "a spouse on this request";
}

/**
 * Every SSN field that repeats somebody else's, keyed by field name, with the
 * sentence to render under it (BRK-S317).
 *
 * Only a field being TYPED carries the error. Two typed owners sharing one both
 * get it, each naming the other; a spouse typed equal to their own owner gets
 * the spouse sentence on the SPOUSE field, and the owner's field stays clean.
 * `roster` should carry typed names (`namedRoster`) so the sentence can say who.
 */
export function duplicateSsnErrors(
  roster: readonly RosterPerson[],
  fields: readonly BondRenderField[],
  values: BondValueBag,
  held: (name: string) => string | undefined,
): Record<string, string> {
  const path = ssnPathOf(fields);
  if (path === null) return {};
  const byName = new Map(fields.map((field) => [field.name, field]));
  const readings: SsnReading[] = [];
  for (const person of roster) {
    const field = byName.get(scopedRosterName(person.ownerId, path));
    const reading = field && readSsn(person, field, values, held);
    if (reading) readings.push(reading);
  }

  const errors: Record<string, string> = {};
  for (const mine of readings) {
    if (!mine.typed) continue;
    for (const other of readings) {
      if (other === mine || !sameSsn(mine, other)) continue;
      // An owner typed equal to their own spouse who is ALSO being typed: the
      // spouse field carries it (their sentence is the specific one).
      if (other.typed && other.person.spouseOf === mine.person.ownerId)
        continue;
      errors[mine.name] ??=
        mine.person.spouseOf === other.person.ownerId
          ? SPOUSE_SSN_MATCHES_OWNER
          : duplicateSsnMessage(rowName(other.person));
    }
  }
  return errors;
}
